/**
 * Sleep an async context for timeoutMs milliseconds
 *
 * Note this does not block but instead sets a timeout
 *
 * @param timeoutMs The timeout in milliseconds
 * @returns {Promise} A promise that resolves after timeoutMs milliseconds
 */
const sleep = (timeoutMs: number) => new Promise((resolve) => setTimeout(resolve, timeoutMs));

/**
 * Retry some async action a fixed number of times on failure
 * @param action The action to retry on failure - should return a promise
 * @param backoff True if the delay on subsequent retries be increased
 * @param jitter True if there should be a random factor applied to retries
 * @param maxAttempts The maximum number of times to attempt an action
 * @param timeout The timeout between retries (in milliseconds)
 * @param shouldRetry A predicate to determine if a failure should be retried (return false to stop retrying)
 */
export const retry = (
	action: (attempt: number, nextTryIn: number | null) => Promise<any>,
	{
		backOff = true,
		jitter = true,
		timeout = 2000,
		maxAttempts = 3,
	}: {
		backOff?: boolean;
		jitter?: boolean;
		timeout?: number;
		maxAttempts?: number;
	} = {},
	shouldRetry?: (error: any) => Promise<boolean>,
) => {
	const recursiveRetry: (attempt: number) => Promise<any> = async (attempt) => {
		let nextTryIn: number | null = null;
		if (attempt < maxAttempts) {
			const jitterFactor = jitter ? 1 + Math.random() : 1;
			const backOffFactor = backOff ? Math.pow(2, attempt) : 1;
			nextTryIn = timeout * backOffFactor * jitterFactor;
		}

		try {
			return await action(attempt, nextTryIn);
		} catch (ex) {
			if (nextTryIn === null) {
				throw ex;
			}

			if (shouldRetry && !(await shouldRetry(ex))) {
				throw ex;
			}

			await sleep(nextTryIn);
			return await recursiveRetry(attempt + 1);
		}
	};

	return recursiveRetry(0);
};
